import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Warning, PencilSimple, Check, X, Clock, User, Phone } from "@phosphor-icons/react";

const daysSince = (iso) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : 0);

function Meta({ label, children, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

// Bloque principal del ticket de Taller: falla reportada + estado + días + técnico.
export default function TicketIssueBlock({ ticket, statusMeta, priorityMeta, editable, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setEditing(false); }, [ticket.id]);

  const fault = (ticket.fault_description || "").trim();
  const reason = (ticket.reason || "").trim();
  const showReason = reason && reason !== fault && !/^Nota de reparación/i.test(reason);
  const days = daysSince(ticket.created_at);
  const daysTone = days >= 14 ? "text-[#E0000F]" : days >= 7 ? "text-amber-600" : "text-zinc-900";
  const cd = ticket.client_device;

  const save = async () => {
    const text = draft.trim();
    if (!text) { toast.error("Describe la falla"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/hospital/tickets/${ticket.id}`, { fault_description: text });
      toast.success("Falla actualizada");
      setEditing(false);
      onChanged?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <section className="border border-amber-200 border-l-4 border-l-[#E0000F] bg-amber-50/60 p-4" data-testid="ticket-issue-block">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800 inline-flex items-center gap-1">
              <Warning size={12} weight="fill" className="text-[#E0000F]" /> ¿Por qué está aquí? · Falla reportada
            </div>
            {editable && !editing && (
              <button type="button" onClick={() => { setDraft(fault); setEditing(true); }} className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500 hover:text-[#002FA7] inline-flex items-center gap-1" data-testid="ticket-fault-edit">
                <PencilSimple size={11} weight="bold" /> Editar
              </button>
            )}
          </div>
          {editing ? (
            <div className="mt-2 space-y-2" data-testid="ticket-fault-editor">
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} autoFocus
                className="w-full text-sm border border-amber-300 bg-white rounded-md p-2 focus:outline-none focus:border-[#002FA7] resize-none" data-testid="ticket-fault-input" />
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={saving} className="h-7 gap-1" data-testid="ticket-fault-cancel"><X size={12} /> Cancelar</Button>
                <Button size="sm" onClick={save} disabled={saving} className="h-7 gap-1 bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="ticket-fault-save"><Check size={12} weight="bold" /> {saving ? "Guardando…" : "Guardar"}</Button>
              </div>
            </div>
          ) : (
            <p className="mt-1.5 text-base sm:text-lg font-semibold text-zinc-900 leading-snug whitespace-pre-line" data-testid="ticket-fault-text">{fault || "—"}</p>
          )}
          {showReason && <div className="text-xs text-zinc-600 mt-1.5"><span className="uppercase tracking-widest text-[9px] font-semibold text-zinc-500 mr-1">Motivo:</span>{reason}</div>}
          {ticket.comments && <div className="text-xs text-zinc-600 mt-1"><span className="uppercase tracking-widest text-[9px] font-semibold text-zinc-500 mr-1">Comentarios:</span>{ticket.comments}</div>}
          {cd?.customer_name && (
            <div className="text-xs text-zinc-700 mt-2 inline-flex items-center gap-2 flex-wrap" data-testid="ticket-issue-client">
              <span className="uppercase tracking-widest text-[9px] font-semibold text-zinc-500">Cliente:</span>
              <span className="font-semibold">{cd.customer_name}</span>
              {cd.customer_phone && <span className="font-mono-data text-zinc-500 inline-flex items-center gap-1"><Phone size={10} /> {cd.customer_phone}</span>}
            </div>
          )}
        </div>
        <div className="sm:w-44 shrink-0 grid grid-cols-3 sm:grid-cols-1 gap-3 sm:border-l sm:border-amber-200 sm:pl-4">
          <Meta label="En taller" testid="ticket-issue-days">
            <div className={`font-heading font-extrabold text-2xl leading-none numeric ${daysTone}`}>{days}<span className="text-xs font-semibold text-zinc-500 ml-1">{days === 1 ? "día" : "días"}</span></div>
            <div className="text-[10px] text-zinc-500 inline-flex items-center gap-1 mt-0.5"><Clock size={9} /> ingresó {new Date(ticket.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short" })}</div>
          </Meta>
          <Meta label="Estado · prioridad" testid="ticket-issue-state">
            <div className="flex flex-wrap gap-1">
              <span className="text-[10px] font-mono-data px-2 py-0.5 rounded" style={{ background: statusMeta.bg, color: statusMeta.color }}>{statusMeta.label}</span>
              <span className="text-[10px] font-mono-data px-2 py-0.5 rounded" style={{ background: priorityMeta.bg, color: priorityMeta.color }}>{priorityMeta.label}</span>
            </div>
          </Meta>
          <Meta label="Técnico" testid="ticket-issue-tech">
            {ticket.technician_name ? (
              <div className="text-sm font-semibold text-zinc-900 inline-flex items-center gap-1 truncate"><User size={12} weight="bold" className="text-[#002FA7] shrink-0" /> {ticket.technician_name}</div>
            ) : (
              <span className="text-sm text-zinc-400 italic">Sin asignar</span>
            )}
          </Meta>
        </div>
      </div>
    </section>
  );
}
